import { inject, Injectable } from '@angular/core';
import { DocumentEntity, SignedByPartial } from '@domain';
import { RaToastService } from '@shared/components';
import { catchError, map, Observable } from 'rxjs';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { DocumentContractApi } from '../../APIs/documents/document-contract.api';
import { DomainTranslateService } from '../../utils/domain-translate.service';

@Injectable({
    providedIn: 'root',
})
export class DocumentContractController {
    // * [API]
    private _api = inject(DocumentContractApi);

    // * [Injects]
    private _utilService = inject(UtilService);
    private _raToastService = inject(RaToastService);
    private _i18n = inject(DomainTranslateService);

    /**
     * Reenvia o codigo de acesso do contrato
     * @param id id do contrato
     * @param email email para qual vai ser enviado
     * @returns Observable<void>
     */
    public resendCode(id: string, email: string): Observable<void> {
        try {
            const errors = [];
            if (!id) errors.push(this._i18n.instant('domain.validation.contractIdRequired'));
            if (!email) errors.push(this._i18n.instant('domain.validation.contractEmailRequired'));
            if (errors.length > 0) throw errors;

            return this._api.resendCode(id, email).pipe(
                map(() =>
                    this._raToastService.success({
                        title: this._i18n.instant('domain.document.contract.success.codeResentTitle'),
                        message: this._i18n.instant('domain.document.contract.success.codeResentMessage'),
                    })
                ),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.contract.errors.resendCode')
                    )
                )
            );
        } catch (error) {
            return this._utilService.handleError(
                error,
                this._i18n.instant('domain.document.contract.errors.resendCode')
            );
        }
    }

    /**
     * Verifica o codigo de acesso do contrato
     * @param id id do contrato
     * @param code codigo de acesso
     * @returns Observable<void>
     */
    public verifyCode(id: string, code: string): Observable<void> {
        try {
            const errors = [];
            if (!id) errors.push(this._i18n.instant('domain.validation.contractIdRequired'));
            if (!code) errors.push(this._i18n.instant('domain.validation.contractAccessCodeRequired'));
            if (errors.length > 0) throw errors;

            return this._api.verifyCode(id, code).pipe(
                map(response => {
                    const contractToken = response.headers.get('contract-token');
                    if (!contractToken)
                        throw this._i18n.instant('domain.validation.contractTempTokenMissing');

                    this._raToastService.success({
                        title: this._i18n.instant('domain.document.contract.success.codeVerifiedTitle'),
                        message: this._i18n.instant(
                            'domain.document.contract.success.codeVerifiedMessage'
                        ),
                    });

                    // Seta no localstorage que esse documento foi verificado
                    localStorage.setItem(`contract_token_${id}`, contractToken);
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.contract.errors.verifyCode')
                    )
                )
            );
        } catch (error) {
            return this._utilService.handleError(
                error,
                this._i18n.instant('domain.document.contract.errors.verifyCode')
            );
        }
    }

    /**
     * Assina o contrato
     * @param id id do contrato
     * @param name nome da pessoa que vai assinar
     * @param fontFamily fonte da assinatura
     * @returns Observable<void>
     */
    public signContract(id: string, name: string, fontFamily: string): Observable<void> {
        try {
            const errors = [];
            if (!name) errors.push(this._i18n.instant('domain.validation.signerNameRequired'));
            if (!fontFamily) errors.push(this._i18n.instant('domain.validation.signerFontRequired'));
            if (errors.length > 0) throw errors;

            // Busca o código do localstorage
            const code = localStorage.getItem(`contract_token_${id}`);
            if (!code) throw this._i18n.instant('domain.validation.signerAccessCodeNotFound');

            return this._api.signContract(name, fontFamily, code).pipe(
                map(() => {
                    this._raToastService.success({
                        title: this._i18n.instant('domain.document.contract.success.signedTitle'),
                        message: this._i18n.instant('domain.document.contract.success.signedMessage'),
                    });
                }),
                catchError(error =>
                    this._utilService.handleError(error, this._i18n.instant('domain.document.contract.errors.sign'))
                )
            );
        } catch (error) {
            return this._utilService.handleError(
                error,
                this._i18n.instant('domain.document.contract.errors.sign')
            );
        }
    }

    /**
     * Verifica o status do assinante do contrato
     * @param id id do contrato
     * @returns Observable<SignedByPartial>
     */
    public verifySigned(id: string): Observable<SignedByPartial> {
        try {
            const errors = [];
            if (!id) errors.push(this._i18n.instant('domain.validation.contractIdRequired'));
            if (errors.length > 0) throw errors;

            return this._api.verifySigned(id).pipe(
                map(response => response.body),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.contract.errors.verifySigner')
                    )
                )
            );
        } catch (error) {
            return this._utilService.handleError(
                error,
                this._i18n.instant('domain.document.contract.errors.verifySigner')
            );
        }
    }

    /**
     * Atualiza a lista de assinantes do contrato
     * @param id id do contrato
     * @param signedBy lista de assinantes
     * @returns Observable<void>
     */
    public updateSignedBy(id: string, signedBy: SignedByPartial[]): Observable<void> {
        try {
            const errors = [];
            if (!id) errors.push(this._i18n.instant('domain.validation.contractIdRequired'));
            if (!signedBy) errors.push(this._i18n.instant('domain.validation.signedByListRequired'));
            if (errors.length > 0) throw errors;

            return this._api.updateSignedBy(id, signedBy).pipe(
                map(() => {
                    this._raToastService.success({
                        title: this._i18n.instant('domain.document.contract.success.signersUpdatedTitle'),
                        message: this._i18n.instant(
                            'domain.document.contract.success.signersUpdatedMessage'
                        ),
                    });
                }),
                catchError(error =>
                    this._utilService.handleError(
                        error,
                        this._i18n.instant('domain.document.contract.errors.updateSignedBy')
                    )
                )
            );
        } catch (error) {
            return this._utilService.handleError(
                error,
                this._i18n.instant('domain.document.contract.errors.updateSignedBy')
            );
        }
    }

    /**
     * Publica o contrato
     * @param id id do contrato
     * @returns Observable<DocumentEntity>
     */
    public publishContract(id: string): Observable<DocumentEntity> {
        return this._api.publishContract(id).pipe(
            map(response => {
                this._raToastService.success({
                    title: this._i18n.instant('domain.document.contract.success.publishedTitle'),
                    message: this._i18n.instant('domain.document.contract.success.publishedMessage'),
                });

                return response;
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.contract.errors.publish')
                )
            )
        );
    }
}
